import { useMemo } from 'react'
import * as React from 'react'
import styled, { keyframes } from 'styled-components'
import { rem } from 'polished'

import TextButton from 'LuxKit/Button/TextButton'
import BodyText from 'LuxKit/Typography/BodyText'
import IconButton from 'LuxKit/Button/IconButton'
import LineAngleLeftIcon from 'LuxKit/Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import type { ImageCarouselInterstitial, ImageCarouselRefInterface } from 'LuxKit/Carousel/BaseImageCarousel'
import BaseImageCarousel from 'LuxKit/Carousel/BaseImageCarousel'
import ImageGalleryThumbnailStrip from 'LuxKit/Carousel/ImageGalleryThumbnailStrip'
import { mediaQueryUp } from 'components/utils/breakpoint'

const SingleViewContainer = styled.div`
  grid-area: modal-body;
  min-height: 0;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  ${mediaQueryUp.tablet} {
    overflow: hidden;
  }
`

const GalleryElement = styled.div`
  padding: ${rem(16)} ${rem(48)};
  display: flex;
  flex-direction: column;
  min-height: 0;
  gap: ${rem(12)};
  overflow: visible;
  min-width: 0;
`

const MediaBlock = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: visible;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0 ${rem(12)};

  > * + * {
    margin-top: ${rem(4)};
  }
`

const MediaRow = styled.div`
  display: grid;
  grid-template-columns: ${rem(40)} 1fr ${rem(40)};
  align-items: center;
  gap: ${rem(16)};
  width: 100%;
`

const GalleryReservedRatio = styled.div`
  overflow: hidden;
  aspect-ratio: 16 / 9;
  margin: 0 auto;
  position: relative;
  height: min(60vh, calc(100vh - ${rem(380)}));
  width: auto;
  max-width: ${rem(920)};
  max-height: calc(100vh - ${rem(380)});
`

const TopSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 ${rem(12)};
`

const BottomRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  padding: 0 ${rem(12)};
  padding-bottom: ${rem(16)};
  min-height: ${rem(20)};

  > * + * {
    margin-top: ${rem(8)};
  }
`

const ThumbnailStripWrapper = styled.div`
  flex-shrink: 0;
  overflow: hidden;
  margin-top: ${rem(12)};
`

const SquareThumbnailStrip = styled(ImageGalleryThumbnailStrip)`
  > div > button {
    border-radius: 0;
  }

  > div > button::before,
  > div > button::after {
    border-radius: 0;
  }
`

const NavigationArrow = styled(IconButton)`
  display: none;
  z-index: 1;
  transition: color 0.2s, background-color 0.2s, border-color 0.2s, opacity 0.2s;

  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }
`

const MediaNavButton = styled(NavigationArrow)`
  position: static;
  transform: none;
`

const SkeletonPulse = keyframes`
  0% {
    opacity: 0.5;
  }
  50% {
    opacity: 1;
  }
  100% {
    opacity: 0.5;
  }
`

const SkeletonBlock = styled.div`
  background: ${props => props.theme.palette.neutral.default.six};
  border-radius: ${rem(8)};
  animation: ${SkeletonPulse} 1.4s ease-in-out infinite;
`

const SingleViewSkeleton = styled(SkeletonBlock)`
  aspect-ratio: 16 / 9;
  width: auto;
  max-width: ${rem(920)};
  height: min(60vh, calc(100vh - ${rem(320)}));
  max-height: calc(100vh - ${rem(320)});
  margin: 0 auto;
`

const galleryImageParams = { mobileAspectRatio: '16:9', quality: 'good', fit: 'center' } as const

interface Props {
  carouselRef: React.RefObject<ImageCarouselRefInterface | null>
  currentImageIndex: number
  currentImageTitle?: string
  currentImages: Array<App.Image>
  currentInterstitials: Array<ImageCarouselInterstitial>
  galleryCounter: string
  highlightColor: string
  onImageChange: (index: number, image?: App.Image) => void
  onNext: () => void
  onPrev: () => void
  onThumbnailSelect: (index: number) => void
  viewRoomCtaLabel?: string
  showViewRoomCta?: boolean
  onViewRoomClick?: () => void
  videoIds: Array<string>
  videoPosition?: number
}

function OfferGallerySectionedSingleView(props: Props) {
  const {
    carouselRef,
    currentImageIndex,
    currentImageTitle,
    currentImages,
    currentInterstitials,
    galleryCounter,
    highlightColor,
    onImageChange,
    onNext,
    onPrev,
    onThumbnailSelect,
    onViewRoomClick,
    showViewRoomCta = false,
    viewRoomCtaLabel,
    videoIds,
    videoPosition,
  } = props

  const totalCount = currentImages.length + currentInterstitials.length
  const hasCurrentMedia = totalCount > 0
  const arrowHidden = totalCount <= 1

  const singleViewContent = useMemo(() => {
    if (!hasCurrentMedia) {
      return (
        <MediaBlock>
          <SingleViewSkeleton />
        </MediaBlock>
      )
    }

    const shouldShowRoomCta = showViewRoomCta && !!viewRoomCtaLabel && !!onViewRoomClick

    return (
      <>
        <TopSection>
          <BodyText variant="medium" colour="neutral-one" align="center">
            {galleryCounter}
          </BodyText>
        </TopSection>
        <MediaBlock>
          <MediaRow>
            <MediaNavButton
              onClick={onPrev}
              kind="secondary"
              variant="dark"
              shape="circle"
              size="medium"
              data-direction="left"
              hidden={arrowHidden}
            >
              <LineAngleLeftIcon />
            </MediaNavButton>
            <GalleryReservedRatio>
              <BaseImageCarousel
                defaultIndex={currentImageIndex}
                imageParams={galleryImageParams}
                carouselRef={carouselRef}
                images={currentImages}
                onImageChange={onImageChange}
                interstitials={currentInterstitials}
              />
            </GalleryReservedRatio>
            <MediaNavButton
              onClick={onNext}
              className="right"
              variant="dark"
              kind="secondary"
              shape="circle"
              size="medium"
              data-direction="right"
              hidden={arrowHidden}
            >
              <LineAngleRightIcon />
            </MediaNavButton>
          </MediaRow>
          <BottomRow>
            <BodyText variant="medium" colour="neutral-one" align="center">
              {currentImageTitle ?? ''}&nbsp;
            </BodyText>
            {shouldShowRoomCta && (
              <TextButton kind="primary" size="medium" onClick={onViewRoomClick}>
                {viewRoomCtaLabel}
              </TextButton>
            )}
          </BottomRow>
        </MediaBlock>
        <ThumbnailStripWrapper>
          <SquareThumbnailStrip
            images={currentImages}
            videoIds={videoIds}
            videoPosition={videoPosition}
            selectedIndex={currentImageIndex}
            onSelect={onThumbnailSelect}
            showScrollArrows
            highlightColor={highlightColor}
          />
        </ThumbnailStripWrapper>
      </>
    )
  }, [
    arrowHidden,
    carouselRef,
    currentImageIndex,
    currentImageTitle,
    currentImages,
    currentInterstitials,
    galleryCounter,
    hasCurrentMedia,
    highlightColor,
    onImageChange,
    onNext,
    onPrev,
    onThumbnailSelect,
    onViewRoomClick,
    showViewRoomCta,
    viewRoomCtaLabel,
    videoIds,
    videoPosition,
  ])

  return (
    <SingleViewContainer>
      <GalleryElement>
        {singleViewContent}
      </GalleryElement>
    </SingleViewContainer>
  )
}

export default OfferGallerySectionedSingleView
